import { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Search, UserPlus,
  CheckCircle2, AlertTriangle, Trash2, Ban, ArrowLeft, Star,
  Pencil, Phone, User as UserIcon, Calendar as CalIcon, Sparkles, Copy, Bell, StickyNote, Users, Globe,
} from "lucide-react";
import confetti from "canvas-confetti";
import api, { formatDetail } from "@/lib/api";
import { formatTime12 } from "@/lib/i18n";
import TimePicker from "@/components/TimePicker";

const DAY_MIN_START = 9 * 60;
const DAY_MIN_END = 21 * 60;   // extended from 7 PM → 9 PM per owner request
const PIXELS_PER_MIN = 2.0;
const SLOT_MIN = 15;
const STAFF_COL_WIDTH = 100;
const STAFF_VISIBLE_DAY = 6;

function fmtDate(d) {
  // Use LOCAL date components — NOT toISOString (which is UTC and rolls over
  // to next day when Florida is late evening). This bug caused tomorrow's
  // bookings to render under "today" on the owner calendar while the SMS
  // still said the correct (customer-picked) date.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeek(d) { const x = new Date(d); const day = x.getDay(); const diff = day === 0 ? -6 : 1 - day; x.setDate(x.getDate() + diff); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function minToHHMM(mins) { return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`; }
function hhmmToMin(hhmm) { const [h, m] = (hhmm || "00:00").split(":").map(Number); return (h || 0) * 60 + (m || 0); }

function ServicesSummaryWithRed({ summary, red, multiline }) {
  // Render the comma-separated services_summary, painting any service whose
  // (case-insensitive) name appears in the `red` list in BRIGHT RED + bold.
  // If `multiline` is true, splits each service onto its own line so the tech
  // can read EVERY service even on a narrow card.
  const text = (summary || "").toString();
  if (!text) return null;
  const redLower = (red || []).map((r) => (r || "").toLowerCase().trim()).filter(Boolean);
  if (multiline) {
    const items = text.split(/\s*,\s*/).filter(Boolean);
    return (
      <div className="flex flex-col leading-snug">
        {items.map((p, i) => {
          const isRed = redLower.some((r) => r && p.toLowerCase().includes(r));
          return (
            <span key={i} className={isRed ? "font-extrabold underline" : ""} style={isRed ? { color: "#FCA5A5" } : undefined}>
              {p}
            </span>
          );
        })}
      </div>
    );
  }
  if (redLower.length === 0) return <>{text}</>;
  const parts = text.split(/(\s*,\s*)/);
  return (
    <>
      {parts.map((p, i) => {
        if (i % 2 === 1) return <span key={i}>{p}</span>; // separator
        const isRed = redLower.some((r) => r && p.toLowerCase().includes(r));
        return isRed
          ? <span key={i} style={{ color: "#FCA5A5" }} className="font-extrabold underline">{p}</span>
          : <span key={i}>{p}</span>;
      })}
    </>
  );
}

function bookingColor(b) {
  if (b.is_block_time) return { stripe: "#3D3531", bg: "#3D3531", text: "white" };
  if (b.status === "no_show") return { stripe: "#B22222", bg: "#B22222", text: "white" };
  // Confirmed = keep ORIGINAL request/assign color so the customer name stays
  // legible; we'll outline the card with a thick gold border via `confirmed`
  // flag (handled in the render). Per owner: keep stamp, only frame turns gold.
  // CHECKED-IN: card body turns muted gray so reception can spot who's already
  // here at a glance, but the LEFT STRIPE keeps the request/assign color
  // (dark-red vs dark-green) so the original intent is still readable. Owner
  // requested this so staff don't lose Assign/Request context after check-in.
  if (b.checked_in) {
    const origStripe = b.requested_any ? "#0F4A2F" : "#7A1818";
    return { stripe: origStripe, bg: "#8B8782", text: "white", checkedIn: true };
  }
  if (!b.requested_any) return { stripe: "#7A1818", bg: "#B22222", text: "white", confirmed: b.status === "confirmed" };  // Request — DARK RED
  return { stripe: "#0F4A2F", bg: "#1B5E3C", text: "white", confirmed: b.status === "confirmed" };  // Assign — DARK GREEN
}

export default function OwnerDashboard() {
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(new Date());
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [counts, setCounts] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  // Quick-view popup (compact info card before opening full editor)
  const [quickView, setQuickView] = useState(null);
  // Yellow flash on a staff column after Walk-in auto-assigns to them.
  // Both the Calendar staff header AND Turn Board sidebar flash for 10 seconds.
  const [flashStaffId, setFlashStaffId] = useState(null);
  const [flashStaffName, setFlashStaffName] = useState(null);
  const triggerFlash = (sid, name) => {
    setFlashStaffId(sid);
    setFlashStaffName(name || null);
    setTimeout(() => { setFlashStaffId(null); setFlashStaffName(null); }, 10000);
    try {
      confetti({
        particleCount: 60, spread: 80, startVelocity: 35,
        origin: { y: 0.3, x: 0.5 },
        colors: ["#c6a760", "#7a8a6e", "#e7b23c", "#b38a5e"],
      });
    } catch (e) { /* cosmetic */ }
    // Tell the embedded Turn Board iframe to flash the same tech for 10 seconds.
    try {
      const tb = document.querySelector('[data-testid="turnboard-iframe"]');
      if (tb && tb.contentWindow && name) {
        tb.contentWindow.postMessage({ type: "flash-staff", name, durationMs: 10000 }, "*");
      }
    } catch (e) { /* iframe cross-origin or not loaded — skip */ }
  };
  // Single-page horizontal scroll layout — show ALL staff in one row; the
  // wrapper scrolls horizontally if there are more staff than fit on screen.
  // (Replaces the earlier sliding pagination — Hong Phuc preferred Excel-style scroll.)

  const range = useMemo(() => {
    if (view === "day") return { date_from: fmtDate(anchor), date_to: fmtDate(anchor) };
    if (view === "week") { const s = startOfWeek(anchor); return { date_from: fmtDate(s), date_to: fmtDate(addDays(s, 6)) }; }
    return { date_from: fmtDate(startOfMonth(anchor)), date_to: fmtDate(endOfMonth(anchor)) };
  }, [view, anchor]);

  const [businessHours, setBusinessHours] = useState([]);
  const loadAll = async () => {
    try {
      const [bs, st, sv, ds, cc, bh] = await Promise.all([
        api.get("/admin/bookings", { params: range }),
        api.get("/admin/staff"),
        api.get("/public/services"),
        api.get("/admin/dashboard-stats"),
        api.get("/admin/calendar-counts", { params: range }),
        api.get("/public/business-hours"),
      ]);
      setBookings(bs.data); setStaff(st.data.filter((s) => s.active));
      setServices(sv.data); setStats(ds.data); setCounts(cc.data);
      setBusinessHours(bh.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Failed to load"); }
  };

  // Independent fetch: counts for the next 12 days (date strip badges).
  // Re-fetches every time we move the anchor (so today's count stays fresh
  // even when the user changes view) and after any successful save.
  const [stripCounts, setStripCounts] = useState({});
  const loadStripCounts = async () => {
    try {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const last = new Date(today); last.setDate(last.getDate() + 11);
      const { data } = await api.get("/admin/calendar-counts", {
        params: { date_from: fmtDate(today), date_to: fmtDate(last) },
      });
      setStripCounts(data || {});
    } catch { /* non-blocking */ }
  };
  useEffect(() => { loadStripCounts(); /* eslint-disable-next-line */ }, [range.date_from, range.date_to]);

  const loadAllRef = useRef(null);
  loadAllRef.current = loadAll;

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [range.date_from, range.date_to]);

  // Keyboard shortcuts: ← / → move the anchored date by 1 (day view),
  // 7 (week view), or 30 (month view). Ignored when a modal/input is focused.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      if (dialogOpen || quickView) return;
      e.preventDefault();
      move(e.key === "ArrowLeft" ? -1 : 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line
  }, [dialogOpen, quickView, view, anchor]);

  // Auto-refresh every 15s while dialog is closed — picks up AI/online bookings without F5
  useEffect(() => {
    if (dialogOpen) return; // don't poll while user is editing
    const id = setInterval(() => {
      if (document.visibilityState === "visible" && loadAllRef.current) {
        loadAllRef.current();
      }
    }, 15000);
    return () => clearInterval(id);
  }, [dialogOpen]);

  const move = (delta) => {
    const x = new Date(anchor);
    if (view === "day") x.setDate(x.getDate() + delta);
    else if (view === "week") x.setDate(x.getDate() + delta * 7);
    else x.setMonth(x.getMonth() + delta);
    setAnchor(x);
  };

  const filteredBookings = bookings.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (b.customer_name || "").toLowerCase().includes(q) || (b.customer_phone || "").includes(q) || b.date.includes(q);
  });

  const openBooking = (b) => {
    // Block-time has no detail to show — go straight to editor
    if (b.is_block_time) { setEditing(b); setCreating(false); setDialogOpen(true); return; }
    setQuickView(b);
  };
  const openEditFromQuickView = () => {
    if (!quickView) return;
    setEditing(quickView); setCreating(false); setQuickView(null); setDialogOpen(true);
  };

  // Duplicate: open the New-booking dialog pre-filled with EVERYTHING from
  // the source booking (customer name + phone + services + staff + time +
  // duration) — reception just hits Save. Use case: the salon's group of
  // friends/family booked under one phone, or making back-to-back identical
  // appointments without re-typing anything. We pass the entire source via
  // the special `_duplicateFrom` field on `creating`; BookingDialog reads it
  // in its open-effect and seeds all internal form state.
  const onDuplicate = (source) => {
    const src = source || quickView;
    if (!src) return;
    setQuickView(null);
    setDialogOpen(false);
    setEditing(null);
    setCreating({
      staff_id: src.staff_id,
      date: src.date,
      start_time: src.start_time,
      duration_minutes: src.duration_minutes,
      _duplicateFrom: src,
    });
    setDialogOpen(true);
  };

  const onCheckin = async (id) => { try { await api.post(`/admin/bookings/${id}/checkin`); toast.success("Checked in"); setDialogOpen(false); setQuickView(null); loadAll(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };
  const onConfirm = async (id) => { try { await api.post(`/admin/bookings/${id}/confirm`); toast.success("Confirmed"); setDialogOpen(false); setQuickView(null); loadAll(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };
  const onNoShow = async (id) => { try { await api.post(`/admin/bookings/${id}/no-show`); toast.success("Marked no-show"); setDialogOpen(false); setQuickView(null); loadAll(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };
  const onLate = async (id) => { try { await api.post(`/admin/bookings/${id}/late`); toast.success("Marked late"); setDialogOpen(false); setQuickView(null); loadAll(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } }; // kept for backward-compat ; unused after Late→Duplicate swap
  void onLate;
  const onCancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      // Block-times live in a separate collection (`block_times`) — their
      // DELETE endpoint is /admin/block-time/{id}. We detect by looking up
      // the booking-or-block in our local state (works because the calendar
      // already merges both via is_block_time flag).
      const item = bookings.find((b) => b.id === id);
      const url = item?.is_block_time ? `/admin/block-time/${id}` : `/admin/bookings/${id}`;
      await api.delete(url);
      toast.success(item?.is_block_time ? "Block removed" : "Cancelled");
      setDialogOpen(false); setQuickView(null); loadAll();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };
  const onRequestReview = async (id) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/request-review`, {});
      if (data.already_sent) toast.info("Review already requested for this booking");
      else if (data.delivered) toast.success("Review SMS sent ⭐");
      else toast.info("Review queued (SMS pending A2P compliance)");
      setDialogOpen(false); setQuickView(null); loadAll();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  // Try patch with optional force=true on conflict
  const patchBooking = async (id, body, opts = {}) => {
    // Detect if this is a block-time → routes to a different endpoint with a
    // different payload shape (needs explicit end_time instead of duration).
    const item = bookings.find((b) => b.id === id);
    if (item?.is_block_time) {
      try {
        const start = body.start_time || item.start_time;
        const dur = body.duration_minutes ?? item.duration_minutes ?? 60;
        const startMin = parseInt(start.split(":")[0]) * 60 + parseInt(start.split(":")[1]);
        const endMin = startMin + dur;
        const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
        await api.patch(`/admin/block-time/${id}`, {
          staff_id: body.staff_id || item.staff_id,
          date: body.date || item.date,
          start_time: start,
          end_time: endTime,
        });
        return true;
      } catch (err) {
        toast.error(formatDetail(err.response?.data?.detail) || "Failed to move block");
        return false;
      }
    }
    const qs = opts.notify ? "?notify=true" : "";
    try {
      await api.patch(`/admin/bookings/${id}${qs}`, body);
      return true;
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "object" && d?.code === "manual_conflict") {
        const conflictsTxt = (d.conflicts || []).map((c) => `${c.customer_name || c.type} ${c.start_time}-${c.end_time}`).join("; ");
        if (window.confirm(`Time slot already has another manual booking (${conflictsTxt}). Place on top anyway?`)) {
          const forceQs = opts.notify ? "?force=true&notify=true" : "?force=true";
          try { await api.patch(`/admin/bookings/${id}${forceQs}`, body); return true; }
          catch (err2) { toast.error(formatDetail(err2.response?.data?.detail)); return false; }
        }
        return false;
      }
      if (typeof d === "object" && d?.code === "online_conflict") {
        toast.error("Cannot move on top of an online/AI booking");
        return false;
      }
      toast.error(formatDetail(d));
      return false;
    }
  };

  // Confirm-reschedule dialog state. Populated by onDropToStaff/onResize
  // *before* the PATCH fires so reception can review the change and (optionally)
  // text the customer about it.
  const [reschedule, setReschedule] = useState(null);
  // reschedule shape: { booking, body, fromLabel, toLabel, fromStaff, toStaff }
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);

  // Pretty date label for the reschedule confirm dialog: "Sat 30 May 2026"
  const fmtDateLong = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  const askReschedule = (booking, body, opts) => {
    // Default the "send SMS" checkbox to OFF per owner request — reception
    // should consciously tick it only when the customer actually needs to be
    // notified. Avoids accidental double-texting when reception is just
    // tidying the calendar.
    setNotifyCustomer(false);
    setReschedule({ booking, body, ...opts });
  };
  const confirmReschedule = async () => {
    if (!reschedule) return;
    setRescheduleBusy(true);
    const ok = await patchBooking(reschedule.booking.id, reschedule.body, { notify: notifyCustomer });
    setRescheduleBusy(false);
    if (ok) {
      toast.success(notifyCustomer ? "Rescheduled + SMS sent" : "Rescheduled");
      setReschedule(null);
      loadAll();
    }
  };

  const onDragStart = (e, b) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const grabOffsetY = e.clientY - rect.top;
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: b.id, grabOffsetY }));
    // Hide the default browser drag preview that sticks to the cursor — user wants only the ghost rectangle in target column
    try {
      const img = new Image();
      img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
      e.dataTransfer.setDragImage(img, 0, 0);
    } catch {}
  };
  const onDropToStaff = async (e, staffId, dateStr) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData("text/plain") || "{}");
    if (!data.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // The cursor position == new START time. Use FLOOR so dropping inside a
    // 15-min cell stays in that cell (not the next one).
    const y = e.clientY - rect.top;
    const rawMin = Math.floor(y / PIXELS_PER_MIN / SLOT_MIN) * SLOT_MIN;
    const newStartMin = Math.max(DAY_MIN_START, Math.min(DAY_MIN_END - SLOT_MIN, DAY_MIN_START + rawMin));
    const newStartTime = minToHHMM(newStartMin);
    const booking = bookings.find((b) => b.id === data.id);
    if (!booking) return;
    // No-op drop (same staff/date/start)? Skip the dialog entirely so a tiny
    // accidental drag doesn't pop a confirm.
    if (booking.staff_id === staffId && booking.date === dateStr && booking.start_time === newStartTime) return;
    const fromStaff = staff.find((s) => s.id === booking.staff_id)?.full_name || "—";
    const toStaff = staff.find((s) => s.id === staffId)?.full_name || "—";
    askReschedule(booking, { staff_id: staffId, date: dateStr, start_time: newStartTime }, {
      fromStaff, toStaff,
      fromDate: booking.date, fromTime: booking.start_time, fromDuration: booking.duration_minutes,
      toDate: dateStr, toTime: newStartTime, toDuration: booking.duration_minutes,
      kind: "move",
    });
  };

  const onSlotRangeSelected = (staffId, dateStr, startTime, endTime) => {
    setEditing(null);
    setCreating({ staff_id: staffId, date: dateStr, start_time: startTime, end_time: endTime });
    setDialogOpen(true);
  };

  const onResize = async (b, newDuration) => {
    if (newDuration === b.duration_minutes) return;
    const staffName = staff.find((s) => s.id === b.staff_id)?.full_name || "—";
    askReschedule(b, { duration_minutes: newDuration }, {
      fromStaff: staffName, toStaff: staffName,
      fromDate: b.date, fromTime: b.start_time, fromDuration: b.duration_minutes,
      toDate: b.date, toTime: b.start_time, toDuration: newDuration,
      kind: "resize",
    });
  };

  return (
    <AdminLayout title="Calendar" fillViewport={view === "day"}>
      {/* Stats row removed per user — keep date nav + booking grid only */}
      <div className={`bg-white rounded-2xl border border-zen-border p-4 mb-4 flex flex-wrap items-center gap-3 ${view === "day" ? "flex-shrink-0" : ""}`}>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" onClick={() => setAnchor(new Date())} data-testid="cal-today" className="rounded-full">Today</Button>
          <Button size="icon" variant="ghost" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="font-serif text-xl text-zen-ink">
          {view === "day" && anchor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          {view === "week" && (() => { const s = startOfWeek(anchor); const e = addDays(s, 6); return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`; })()}
          {view === "month" && anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </div>
        <Tabs value={view} onValueChange={setView} className="ml-auto">
          <TabsList data-testid="view-tabs">
            <TabsTrigger value="day" data-testid="view-day">Day</TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week">Week</TabsTrigger>
            <TabsTrigger value="month" data-testid="view-month">Month</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zen-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-9 h-9 w-40 rounded-full bg-zen-surface border-0" data-testid="search-input" />
        </div>
        <Button size="sm" variant="outline" onClick={() => { setCreating("block"); setEditing(null); setDialogOpen(true); }} className="rounded-full" data-testid="block-btn">
          <Ban className="w-4 h-4 mr-1" /> Block
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setCreating("walk"); setEditing(null); setDialogOpen(true); }} className="rounded-full" data-testid="walkin-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Walkin
        </Button>
      </div>

      {/* Quick-jump date strip — 12 days starting today. Today is highlighted in Zen primary.
          Receptionists tap here to switch days fast without using the calendar arrows,
          avoiding the "I accidentally booked on the wrong day" mistake. */}
      {view === "day" && (
        <div className="bg-white rounded-2xl border border-zen-border px-2 py-2 mb-4 flex-shrink-0 overflow-x-auto" data-testid="date-strip">
          <div className="flex gap-1.5 min-w-max">
            {Array.from({ length: 20 }, (_, i) => {
              const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i);
              const isToday = i === 0;
              const isActive = fmtDate(d) === fmtDate(anchor);
              const label = isToday ? "Today" : `${d.getMonth() + 1}/${d.getDate()}`;
              const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
              const count = stripCounts[fmtDate(d)] || 0;
              const isBusy = count >= 10;
              return (
                <button
                  key={i}
                  onClick={() => setAnchor(new Date(d))}
                  className={`flex flex-col items-center justify-center min-w-[72px] px-3 py-1.5 rounded-xl border transition
                    ${isActive
                      ? "bg-zen-primary text-white border-zen-primary shadow-md font-bold"
                      : isToday
                        ? "bg-zen-primary/10 text-zen-primary border-zen-primary font-bold ring-2 ring-zen-primary/40"
                        : isBusy
                          ? "bg-amber-50 text-zen-ink border-amber-300 hover:border-zen-primary"
                          : "bg-white text-zen-ink border-zen-border hover:border-zen-primary hover:bg-zen-surface"}`}
                  data-testid={`date-strip-${i}`}
                >
                  <span className="text-[10px] uppercase tracking-wider opacity-80 leading-none">{weekday}</span>
                  <span className="text-sm leading-tight mt-0.5">{label}</span>
                  <span className={`text-[10px] leading-none mt-1 font-mono ${
                    isActive ? "text-white/90"
                    : count === 0 ? "text-zen-muted/50"
                    : isBusy ? "text-amber-700 font-bold"
                    : "text-zen-muted"}`}
                  data-testid={`date-strip-count-${i}`}
                  >
                    {count === 0 ? "—" : `${count} ${count === 1 ? "appt" : "appts"}`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === "day" && <DayView date={anchor} staff={staff} bookings={filteredBookings.filter((b) => b.date === fmtDate(anchor))} businessHours={businessHours} onClick={openBooking} onDragStart={onDragStart} onDrop={onDropToStaff} onSlotRangeSelected={onSlotRangeSelected} onResize={onResize} flashStaffId={flashStaffId} />}
      {view === "week" && <WeekView anchor={anchor} bookings={filteredBookings} onClick={openBooking} staff={staff} />}
      {view === "month" && <MonthView anchor={anchor} counts={counts} onPick={(d) => { setAnchor(d); setView("day"); }} />}

      <BookingDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        booking={editing} creating={creating} services={services} staff={staff}
        onSaved={() => { setDialogOpen(false); loadAll(); }}
        onCheckin={onCheckin} onConfirm={onConfirm} onNoShow={onNoShow} onDuplicate={onDuplicate} onCancel={onCancel}
        onRequestReview={onRequestReview}
        onWalkinAssigned={(sid, sname) => { setDialogOpen(false); triggerFlash(sid, sname); loadAll(); }}
      />
      <BookingQuickView
        booking={quickView}
        services={services}
        staff={staff}
        onClose={() => setQuickView(null)}
        onEdit={openEditFromQuickView}
        onDuplicate={onDuplicate}
        onCheckin={onCheckin}
        onConfirm={onConfirm}
        onRequestReview={onRequestReview}
      />
      {/* Confirm Reschedule — shown whenever reception drags a booking to a
          new time/staff OR resizes it to a new duration. Lets reception text
          the customer about the change in the same step. */}
      <Dialog open={!!reschedule} onOpenChange={(v) => !v && setReschedule(null)}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Confirm Reschedule</DialogTitle></DialogHeader>
          {reschedule && (
            <div className="space-y-3 text-sm">
              <div className="bg-zen-surface rounded-xl p-3">
                <div className="text-xs uppercase tracking-wider font-semibold text-zen-muted mb-1">From</div>
                <div className="font-semibold text-zen-ink">{reschedule.fromStaff}</div>
                <div className="text-zen-ink/80">
                  {fmtDateLong(reschedule.fromDate)}, {formatTime12(reschedule.fromTime)} ({reschedule.fromDuration} minutes)
                </div>
              </div>
              <div className="bg-zen-primary/10 border border-zen-primary/30 rounded-xl p-3">
                <div className="text-xs uppercase tracking-wider font-semibold text-zen-primary mb-1">To</div>
                <div className="font-semibold text-zen-ink">{reschedule.toStaff}</div>
                <div className="text-zen-ink/80">
                  {fmtDateLong(reschedule.toDate)}, {formatTime12(reschedule.toTime)} ({reschedule.toDuration} minutes)
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  className="w-4 h-4" data-testid="reschedule-notify" />
                <span className="text-sm text-zen-ink">Send notification to the customer</span>
              </label>
              {reschedule.booking?.customer_phone ? (
                <div className="text-[11px] text-zen-muted pl-6">
                  SMS to {reschedule.booking.customer_phone}
                </div>
              ) : (
                <div className="text-[11px] text-zen-muted pl-6">
                  (No phone on file — SMS won't send even if ticked)
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReschedule(null)} data-testid="reschedule-cancel">Cancel</Button>
            <Button onClick={confirmReschedule} disabled={rescheduleBusy}
              className="bg-zen-primary text-white" data-testid="reschedule-confirm">
              {rescheduleBusy ? "Saving…" : "Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-zen-border p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.2em] text-zen-muted">{label}</div>
      <div className="font-serif text-3xl text-zen-ink mt-2">{value}</div>
      {sub && <div className="text-xs text-zen-muted mt-1">{sub}</div>}
    </div>
  );
}

function DayView({ date, staff: allStaff, bookings, businessHours, onClick, onDragStart, onDrop, onSlotRangeSelected, onResize, flashStaffId }) {
  const totalMin = DAY_MIN_END - DAY_MIN_START;
  const hours = []; for (let m = DAY_MIN_START; m <= DAY_MIN_END; m += 60) hours.push(m);
  const slots = []; for (let m = DAY_MIN_START; m < DAY_MIN_END; m += SLOT_MIN) slots.push(m);
  const scrollRef = useRef(null);

  // Resolve TODAY's business hours so we can dim the time outside open hours
  // (matches the screenshot the owner shared — grey overlay over closed time
  // bands, so reception can't accidentally book at 8 AM on Saturday).
  // CONVENTION: backend stores `day` in Python `date.weekday()` format:
  // 0=Monday … 6=Sunday. JS `Date.getDay()` is the opposite (0=Sun…6=Sat),
  // so we convert. The Business Hours settings page (DAYS array
  // `["Monday",…,"Sunday"]`) follows the Mon-based convention too.
  const wdayMonBased = (date.getDay() + 6) % 7;
  const todayBh = (businessHours || []).find((b) => b.day === wdayMonBased);
  const openMin = todayBh && !todayBh.closed ? hhmmToMin(todayBh.open) : DAY_MIN_START;
  const closeMin = todayBh && !todayBh.closed ? hhmmToMin(todayBh.close) : DAY_MIN_END;
  const isClosed = !todayBh || todayBh.closed;
  // Pixel offsets for the two dim bands (before open, after close)
  const dimTopHeight = Math.max(0, (openMin - DAY_MIN_START) * PIXELS_PER_MIN);
  const dimBottomTop = (closeMin - DAY_MIN_START) * PIXELS_PER_MIN;
  const dimBottomHeight = Math.max(0, totalMin * PIXELS_PER_MIN - dimBottomTop);

  // Hide columns for techs who are OFF this specific weekday (saves screen
  // real estate; reception only sees who's actually working that day).
  // EXCEPTION: if a tech is off today but already has at least one booking on
  // this day (e.g. emergency add by owner), KEEP the column so reception can
  // see the booking.
  const staff = useMemo(() => {
    const wdayMon = (date.getDay() + 6) % 7; // 0=Mon … 6=Sun
    const dStr = fmtDate(date);
    const hasBookingToday = new Set(
      bookings.filter((b) => b.date === dStr).map((b) => b.staff_id)
    );
    return (allStaff || []).filter((s) => {
      const isOff = (s.off_weekdays || []).includes(wdayMon);
      return !isOff || hasBookingToday.has(s.id);
    });
  }, [allStaff, date, bookings]);

  // Arrow visibility tracking (Bukkii-style horizontal navigation).
  // Re-checks on scroll, on staff-list change, and on container resize.
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const atStart = el.scrollLeft <= 1;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setShowLeftArrow(!atStart);
      setShowRightArrow(!atEnd && el.scrollWidth > el.clientWidth);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
  }, [staff.length]);

  const scrollByCols = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(150, Math.round(el.clientWidth * 0.8));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Real-time "now" line (red) — always uses Florida (America/New_York)
  // local time regardless of the receptionist's device timezone, since the
  // salon physically operates there. Refresh every 15s for smoother movement.
  const floridaNowMinutes = () => {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(new Date());
      let h = 0, m = 0;
      for (const p of parts) { if (p.type === "hour") h = parseInt(p.value, 10); if (p.type === "minute") m = parseInt(p.value, 10); }
      return (h % 24) * 60 + m;
    } catch {
      const n = new Date(); return n.getHours() * 60 + n.getMinutes();
    }
  };
  const [nowMin, setNowMin] = useState(floridaNowMinutes);
  useEffect(() => {
    const tick = () => setNowMin(floridaNowMinutes());
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);
  // Measure the actual sticky header height so the red NOW-line lands at
  // the correct vertical position. Previously hardcoded 41px which under-
  // measured the avatar+name header (~70px) and pulled the line ~28px / 14
  // minutes upward — owner reported 18:51 visually appearing at 18:37.
  const headerRowRef = useRef(null);
  const [headerH, setHeaderH] = useState(70);
  useEffect(() => {
    if (!headerRowRef.current) return;
    const measure = () => setHeaderH(headerRowRef.current?.offsetHeight || 70);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headerRowRef.current);
    return () => ro.disconnect();
  }, []);
  const isToday = fmtDate(date) === fmtDate(new Date());
  const showNowLine = isToday && nowMin >= DAY_MIN_START && nowMin <= DAY_MIN_END;
  const nowTop = (nowMin - DAY_MIN_START) * PIXELS_PER_MIN;

  // drag-create selection state per column
  const [selecting, setSelecting] = useState(null); // {staffId, dateStr, startMin, endMin}
  // ghost preview state when dragging existing booking
  const [dragGhost, setDragGhost] = useState(null); // {staffId, top, height, label}
  // resize state — single source of truth
  const [resizing, setResizing] = useState(null);
  // Suppress click on booking card right after resize finishes
  const justResizedRef = useRef(false);

  // Snap a Y-pixel offset to the slot it lands IN (floor, not nearest).
  // Each 15-min cell is exactly SLOT_MIN tall in pixels — clicking anywhere
  // inside the top quarter must stay :00, second quarter :15, third :30,
  // bottom :45. Math.round would jump to the next cell at the halfway point.
  const yToMinSnapped = (y) => {
    const rawMin = Math.floor(y / PIXELS_PER_MIN / SLOT_MIN) * SLOT_MIN;
    return Math.max(DAY_MIN_START, Math.min(DAY_MIN_END, DAY_MIN_START + rawMin));
  };

  // Auto-scroll the calendar container while dragging a booking card near the
  // top or bottom edge. The HTML5 drag API doesn't scroll the page on its own,
  // so reception couldn't drop onto time slots that were below the visible
  // viewport. We fire a rAF loop driven by the latest cursor Y captured from
  // dragover events on the scroll container.
  const autoScrollRef = useRef({ raf: 0, clientY: 0, active: false });
  const stopAutoScroll = () => {
    autoScrollRef.current.active = false;
    if (autoScrollRef.current.raf) {
      cancelAnimationFrame(autoScrollRef.current.raf);
      autoScrollRef.current.raf = 0;
    }
  };
  useEffect(() => () => stopAutoScroll(), []);
  const startAutoScroll = () => {
    if (autoScrollRef.current.active) return;
    autoScrollRef.current.active = true;
    const EDGE = 80;         // px from edge that triggers scroll
    const MAX_SPEED = 16;    // px per frame
    const step = () => {
      if (!autoScrollRef.current.active) return;
      const el = scrollRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const y = autoScrollRef.current.clientY;
        const fromTop = y - rect.top;
        const fromBottom = rect.bottom - y;
        let dy = 0;
        if (fromTop >= 0 && fromTop < EDGE) {
          dy = -Math.ceil(((EDGE - fromTop) / EDGE) * MAX_SPEED);
        } else if (fromBottom >= 0 && fromBottom < EDGE) {
          dy = Math.ceil(((EDGE - fromBottom) / EDGE) * MAX_SPEED);
        }
        if (dy !== 0) el.scrollTop += dy;
      }
      autoScrollRef.current.raf = requestAnimationFrame(step);
    };
    autoScrollRef.current.raf = requestAnimationFrame(step);
  };

  return (
    <div className="bg-white rounded-2xl border border-zen-border relative flex-1 min-h-0 flex flex-col" data-testid="dayview-root">
      {/* Bukkii-style horizontal navigation arrows. Overlay INSIDE the scroll
          container, vertically centered, hidden when at start / end. */}
      {showLeftArrow && (
        <button
          type="button"
          onClick={() => scrollByCols(-1)}
          className="absolute z-30 left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/75 text-white flex items-center justify-center shadow-lg transition-colors"
          data-testid="cal-scroll-left"
          aria-label="Scroll calendar left">
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {showRightArrow && (
        <button
          type="button"
          onClick={() => scrollByCols(1)}
          className="absolute z-30 right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/75 text-white flex items-center justify-center shadow-lg transition-colors"
          data-testid="cal-scroll-right"
          aria-label="Scroll calendar right">
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
      {/* Single scroll container — both axes auto. flex-1 + min-h-0 lets it
          fill whatever vertical space the parent <main> gives us (the
          AdminLayout fillViewport prop handles the chain).
          Sticky header + sticky time column stay pinned to the container's
          scroll port (Google-Sheets pattern). */}
      <div
        ref={scrollRef}
        className="overflow-auto flex-1 min-h-0"
        onDragOver={(e) => {
          // Capture latest cursor Y and ensure auto-scroll loop is running
          // whenever a drag passes over the calendar (HTML5 drag has no
          // dragmove, so we use dragover on the scroll container).
          autoScrollRef.current.clientY = e.clientY;
          startAutoScroll();
        }}
        onDrop={stopAutoScroll}
        onDragEnd={stopAutoScroll}
        onDragLeave={(e) => {
          // Only stop when leaving the container entirely (not its children)
          if (e.currentTarget === e.target) stopAutoScroll();
        }}
        data-testid="day-scroll-container">
        <div className="grid relative" style={{ gridTemplateColumns: `64px repeat(${staff.length || 1}, ${STAFF_COL_WIDTH}px)`, minWidth: 64 + (staff.length || 1) * STAFF_COL_WIDTH }}>
          {/* RED NOW-LINE — absolute over entire grid, only when viewing today.
              `top` uses the measured header height so the line lands exactly at
              the current time row regardless of header content changes. */}
          {showNowLine && staff.length > 0 && (
            <div
              className="absolute left-0 right-0 z-30 pointer-events-none"
              style={{ top: `${headerH + nowTop}px` }}
              data-testid="now-line"
            >
              <div className="flex items-center">
                <div className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-r-full -ml-0.5 shadow">
                  {minToHHMM(nowMin)}
                </div>
                <div className="flex-1 h-[2px] bg-red-600 shadow-sm" />
                <div className="w-2.5 h-2.5 -mr-1 rounded-full bg-red-600 shadow" />
              </div>
            </div>
          )}

          <div ref={headerRowRef} className="border-r border-b border-zen-border p-2 bg-zen-surface sticky left-0 top-0 z-30" data-testid="corner-cell"></div>
          {staff.map((s) => {
            const initials = (s.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
            const sexClr = s.sex === "female" ? "bg-zen-cal-red/20 text-zen-cal-red" : s.sex === "male" ? "bg-zen-primary/20 text-zen-primary" : "bg-zen-surface text-zen-ink";
            const isFlash = flashStaffId === s.id;
            return (
              <div key={s.id} className={`border-r border-b border-zen-border sticky top-0 z-20 px-2 py-2.5 flex flex-col items-center gap-1 transition-colors ${isFlash ? "animate-hint-flash-header" : "bg-zen-surface"}`} data-testid={`day-col-header-${s.id}`}>
                <div className={`w-8 h-8 rounded-full ${sexClr} flex items-center justify-center font-bold text-xs ring-2 ring-white shadow-sm`}>
                  {initials}
                </div>
                <div className={`font-serif text-[14px] w-full text-center leading-tight break-words ${isFlash ? "text-zen-ink font-bold" : "text-zen-ink"}`}>
                  {s.full_name}
                </div>
              </div>
            );
          })}

          <div className="border-r border-zen-border relative sticky left-0 bg-white z-10" style={{ height: totalMin * PIXELS_PER_MIN }}>
            {hours.map((h) => {
              const hh = Math.floor(h / 60);
              const period = hh >= 12 ? "PM" : "AM";
              const hour12 = hh % 12 === 0 ? 12 : hh % 12;
              return (
                <div key={h} className="absolute left-0 right-0 border-t-2 border-zen-border text-base font-bold text-zen-ink pl-1.5 pt-0.5"
                  style={{ top: (h - DAY_MIN_START) * PIXELS_PER_MIN }}>
                  {hour12} {period}
                </div>
              );
            })}
          </div>

          {staff.map((s) => (
            <div key={s.id} className={`border-r border-zen-border relative ${flashStaffId === s.id ? "animate-hint-flash" : ""}`} style={{ height: totalMin * PIXELS_PER_MIN }}
              onDragOver={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const startMin = yToMinSnapped(y);
                setDragGhost({ staffId: s.id, top: (startMin - DAY_MIN_START) * PIXELS_PER_MIN, height: 30 * PIXELS_PER_MIN, label: formatTime12(minToHHMM(startMin)) });
              }}              onDragLeave={() => setDragGhost(null)}
              onDrop={(e) => { setDragGhost(null); onDrop(e, s.id, fmtDate(date)); }}
              onMouseDown={(e) => {
                // Start drag-to-create only when clicking on the empty area (not on a booking card)
                if (e.target.closest("[data-booking]")) return;
                if (e.target.closest("[data-resize-handle]")) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const startMin = yToMinSnapped(y);
                setSelecting({ staffId: s.id, dateStr: fmtDate(date), startMin, endMin: startMin + SLOT_MIN, anchor: startMin });
              }}
              onMouseMove={(e) => {
                if (!selecting || selecting.staffId !== s.id) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const m = yToMinSnapped(y);
                const start = Math.min(selecting.anchor, m);
                const end = Math.max(selecting.anchor + SLOT_MIN, m);
                setSelecting({ ...selecting, startMin: start, endMin: end });
              }}
              onMouseUp={() => {
                if (!selecting || selecting.staffId !== s.id) return;
                const sel = selecting;
                setSelecting(null);
                onSlotRangeSelected(sel.staffId, sel.dateStr, minToHHMM(sel.startMin), minToHHMM(sel.endMin));
              }}
              onMouseLeave={() => setSelecting(null)}
              data-testid={`day-col-${s.id}`}>
              {/* Business-hours dim — covers ALL staff columns equally,
                  before open and after close. Darker overlay (was 0.10 →
                  0.32) so the "closed" bands are immediately visible at a
                  glance per owner request. Distinct from per-staff off-
                  hours so reception can tell apart "salon closed" from
                  "this stylist hasn't started yet". */}
              {!isClosed && dimTopHeight > 0 && (
                <div className="absolute left-0 right-0 z-[3] pointer-events-none"
                  style={{ top: 0, height: dimTopHeight, background: "rgba(20,18,16,0.32)" }} />
              )}
              {!isClosed && dimBottomHeight > 0 && (
                <div className="absolute left-0 right-0 z-[3] pointer-events-none"
                  style={{ top: dimBottomTop, height: dimBottomHeight, background: "rgba(20,18,16,0.32)" }} />
              )}
              {isClosed && (
                <div className="absolute left-0 right-0 z-[3] pointer-events-none flex items-center justify-center"
                  style={{ top: 0, height: totalMin * PIXELS_PER_MIN, background: "rgba(20,18,16,0.40)" }}>
                  <span className="bg-white/90 text-zen-muted text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-semibold">Closed today</span>
                </div>
              )}
              {/* Off-hours overlays (custom staff hours like Bibi from 11 AM) + weekly off-days */}
              {(() => {
                const overlays = [];
                const colDate = new Date(fmtDate(date) + "T00:00:00");
                const wdayJs = colDate.getDay(); // JS: 0=Sun, 1=Mon...
                const wdayMon = (wdayJs + 6) % 7; // convert to 0=Mon...6=Sun
                const isOff = (s.off_weekdays || []).includes(wdayMon);
                if (isOff) {
                  const wdayName = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][wdayMon];
                  overlays.push({ key: "off-day", top: 0, height: totalMin * PIXELS_PER_MIN, label: `Day off (${wdayName})` });
                } else {
                  const wsMin = s.work_start ? (parseInt(s.work_start.split(":")[0]) * 60 + parseInt(s.work_start.split(":")[1])) : null;
                  const weMin = s.work_end ? (parseInt(s.work_end.split(":")[0]) * 60 + parseInt(s.work_end.split(":")[1])) : null;
                  if (wsMin && wsMin > DAY_MIN_START) {
                    overlays.push({ key: "off-start", top: 0, height: (wsMin - DAY_MIN_START) * PIXELS_PER_MIN, label: `Off until ${formatTime12(s.work_start)}` });
                  }
                  if (weMin && weMin < DAY_MIN_END) {
                    overlays.push({ key: "off-end", top: (weMin - DAY_MIN_START) * PIXELS_PER_MIN, height: (DAY_MIN_END - weMin) * PIXELS_PER_MIN, label: `Off after ${formatTime12(s.work_end)}` });
                  }
                }
                return overlays.map((o) => (
                  <div key={o.key} className="absolute left-0 right-0 z-[5] pointer-events-none"
                    style={{ top: o.top, height: o.height, background: "repeating-linear-gradient(135deg, rgba(110,102,95,0.10) 0 6px, rgba(110,102,95,0.18) 6px 12px)" }}>
                    <div className="px-1.5 py-0.5 text-[9px] font-semibold text-zen-muted uppercase tracking-wider">{o.label}</div>
                  </div>
                ));
              })()}
              {slots.map((m) => {
                const isHourLine = (m - DAY_MIN_START) % 60 === 0;
                const top = (m - DAY_MIN_START) * PIXELS_PER_MIN;
                const time = minToHHMM(m);
                return (
                  <div key={m}
                    className={`absolute left-0 right-0 ${isHourLine ? "border-t-2 border-zen-border" : "border-t border-dashed border-zen-border/40"}`}
                    style={{ top, height: SLOT_MIN * PIXELS_PER_MIN }}
                    data-testid={`slot-${s.id}-${time}`} />
                );
              })}

              {/* Drag-to-create selection box */}
              {selecting && selecting.staffId === s.id && (
                <div className="absolute left-1 right-1 rounded-md border-2 border-dashed border-zen-primary bg-zen-primary/10 pointer-events-none z-20"
                  style={{ top: (selecting.startMin - DAY_MIN_START) * PIXELS_PER_MIN, height: (selecting.endMin - selecting.startMin) * PIXELS_PER_MIN }}>
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-zen-ink bg-white/85 rounded-md">
                    {formatTime12(minToHHMM(selecting.startMin))} – {formatTime12(minToHHMM(selecting.endMin))}
                  </div>
                </div>
              )}

              {/* Drag preview ghost (when moving an existing booking) */}
              {dragGhost && dragGhost.staffId === s.id && (
                <div className="absolute left-1 right-1 rounded-md border-2 border-dashed border-zen-primary bg-zen-primary/15 pointer-events-none z-20"
                  style={{ top: dragGhost.top, height: dragGhost.height }}>
                  <div className="px-2 py-1 text-xs font-bold text-zen-ink bg-white/90 rounded-t-md">{dragGhost.label}</div>
                </div>
              )}

              {(() => {
                // Detect overlaps per staff column. Group overlapping cards
                // into clusters and assign each one a column index so they
                // render side-by-side (50/50, 33/33/33 ...) instead of
                // stacking. Owner request: when cards overlap reception
                // couldn't grab the resize handle of the underneath card —
                // splitting side-by-side fixes that and still keeps the
                // start/end time visible.
                const list = bookings.filter((b) => b.staff_id === s.id);
                const overlapIds = new Set();
                // Assign each booking a horizontal "lane" within a cluster
                const lane = {};       // bookingId -> lane index (0-based)
                const laneCount = {};  // bookingId -> total lanes in its cluster
                const startMinOf = (bb) => parseInt(bb.start_time.split(":")[0]) * 60 + parseInt(bb.start_time.split(":")[1]);
                const endMinOf = (bb) => startMinOf(bb) + (bb.duration_minutes || 0);
                const visited = new Set();
                for (const a of list) {
                  if (visited.has(a.id)) continue;
                  // BFS cluster of bookings reachable via time-overlap
                  const cluster = [];
                  const queue = [a];
                  while (queue.length) {
                    const cur = queue.shift();
                    if (visited.has(cur.id)) continue;
                    visited.add(cur.id);
                    cluster.push(cur);
                    for (const other of list) {
                      if (visited.has(other.id)) continue;
                      if (startMinOf(cur) < endMinOf(other) && startMinOf(other) < endMinOf(cur)) {
                        queue.push(other);
                      }
                    }
                  }
                  // Sort cluster by start time then duration, assign lanes
                  cluster.sort((x, y) => startMinOf(x) - startMinOf(y) || endMinOf(y) - endMinOf(x));
                  const laneEnd = []; // index = lane id, value = last endMin
                  for (const item of cluster) {
                    const st = startMinOf(item);
                    let assigned = -1;
                    for (let i = 0; i < laneEnd.length; i++) {
                      if (laneEnd[i] <= st) { assigned = i; break; }
                    }
                    if (assigned === -1) { assigned = laneEnd.length; laneEnd.push(0); }
                    laneEnd[assigned] = endMinOf(item);
                    lane[item.id] = assigned;
                  }
                  for (const item of cluster) {
                    laneCount[item.id] = laneEnd.length;
                    if (laneEnd.length > 1) overlapIds.add(item.id);
                  }
                }
                // Family group detection: any phone with 2+ bookings on the
                // SAME day across ALL staff. Owner request: customers
                // booking under one phone for family members should show a
                // group icon so reception spots them at a glance.
                const phoneCountToday = bookings.reduce((acc, bb) => {
                  const ph = (bb.customer_phone || "").trim();
                  if (!ph || ph === "0000000000" || bb.is_block_time) return acc;
                  acc[ph] = (acc[ph] || 0) + 1;
                  return acc;
                }, {});
                return list.map((b) => {
                const start = parseInt(b.start_time.split(":")[0]) * 60 + parseInt(b.start_time.split(":")[1]);
                const top = Math.max(0, (start - DAY_MIN_START) * PIXELS_PER_MIN);
                const liveDuration = (resizing && resizing.bookingId === b.id) ? resizing.currentDuration : b.duration_minutes;
                const h = Math.max(20, liveDuration * PIXELS_PER_MIN);
                const c = bookingColor(b);
                const isOverlap = overlapIds.has(b.id);
                const myLane = lane[b.id] || 0;
                const myLanes = laneCount[b.id] || 1;
                const laneWidthPct = 100 / myLanes;
                const laneLeftPct = laneWidthPct * myLane;
                const stamps = [];
                if (!b.is_block_time) {
                  if (!b.requested_any) stamps.push({ key: "req", text: "REQUEST", color: "#8B5440" });
                  if (b.late) stamps.push({ key: "late", text: "LATE", color: "#B8860B" });
                  if (b.status === "confirmed") stamps.push({ key: "conf", text: "CONFIRMED", color: "#0E6B43" });
                  if (b.checked_in) stamps.push({ key: "in", text: "CHECKED-IN", color: "#6B6862" });
                  if (b.status === "no_show") stamps.push({ key: "ns", text: "NO-SHOW", color: "#9C2E2E" });
                  // Combo/group link badge — orange so it stands out next to
                  // the existing red/green stamps. Lets reception immediately
                  // see "this customer has another card with another stylist".
                  if (b.group_id) stamps.push({ key: "combo", text: "COMBO", color: "#C2410C" });
                }
                const endMin = start + liveDuration;
                const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
                return (
                  <div key={b.id} draggable={true}
                    data-booking
                    onDragStart={(e) => onDragStart(e, b)}
                    onDragEnd={stopAutoScroll}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (justResizedRef.current) {
                        // Suppress edit-dialog popup right after a resize gesture
                        justResizedRef.current = false;
                        return;
                      }
                      onClick(b);
                    }}
                    className={`absolute rounded-lg cursor-grab hover:shadow-md z-10 overflow-hidden ${c.confirmed ? "border-[3px] ring-2 ring-amber-300/60" : "border"}`}
                    style={{
                      top,
                      height: h,
                      left: `calc(${laneLeftPct}% + 4px)`,
                      width: `calc(${laneWidthPct}% - 8px)`,
                      background: c.bg,
                      borderColor: c.confirmed ? "#F59E0B" : c.stripe,
                      color: c.text,
                      // When split into lanes the cards no longer truly
                      // overlap, but slight opacity helps the receptionist
                      // visually parse the cluster as "shared time".
                      opacity: isOverlap ? 0.92 : 1,
                    }}
                    data-testid={`booking-${b.id}`}>
                    {/* Left color stripe */}
                    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: c.stripe }} />
                    <div className="pl-2 pr-1.5 py-1 h-full flex flex-col">
                      <div className="font-bold text-[13px] leading-tight break-words flex items-center gap-1 flex-wrap" style={{ wordBreak: "break-word" }}>
                        {b.is_block_time ? "🚫 Blocked" : (
                          <>
                            <span>{b.customer_name}</span>
                            {b.source === "online" && (
                              <span
                                className="bg-sky-200 text-sky-900 text-[9px] font-bold px-1 rounded leading-none py-0.5 shrink-0 flex items-center gap-0.5 shadow-sm"
                                title="Booked online by the customer"
                                data-testid={`source-online-${b.id}`}>
                                <Globe className="w-2.5 h-2.5" strokeWidth={2.5} />
                              </span>
                            )}
                            {(() => {
                              const cnt = phoneCountToday[(b.customer_phone || "").trim()] || 0;
                              return cnt >= 2 ? (
                                <span
                                  className="bg-purple-200 text-purple-900 text-[9px] font-bold px-1 rounded leading-none py-0.5 shrink-0 flex items-center gap-0.5 shadow-sm"
                                  title={`${cnt} bookings under this phone today`}
                                  data-testid={`family-tag-${b.id}`}>
                                  <Users className="w-2.5 h-2.5" strokeWidth={2.5} />{cnt}
                                </span>
                              ) : null;
                            })()}
                            {(b.customer_notes || "").trim() && (
                              <span
                                className="bg-amber-300 text-amber-900 text-[9px] font-bold px-1 rounded leading-none py-0.5 shrink-0 flex items-center gap-0.5 shadow-sm"
                                title={b.customer_notes}
                                data-testid={`note-bell-${b.id}`}>
                                <Bell className="w-2.5 h-2.5" strokeWidth={2.5} fill="currentColor" />
                              </span>
                            )}
                            {(b.customer_blacklisted || (b.customer_staff_note || "").trim()) && (
                              <span
                                className="bg-red-700 text-white text-[9px] font-extrabold px-1 rounded leading-none py-0.5 shrink-0 flex items-center gap-0.5 shadow-sm border border-red-900"
                                title={b.customer_blacklisted
                                  ? `⚠ BLACKLISTED — ${(b.customer_staff_note || "").trim() || "Do not accept"}`
                                  : `⚠ Sổ đen / Staff note: ${(b.customer_staff_note || "").trim()}`}
                                data-testid={`sodden-tag-${b.id}`}>
                                <AlertTriangle className="w-2.5 h-2.5" strokeWidth={3} fill="currentColor" />
                              </span>
                            )}
                            {b.customer_review_requested && (
                              <span
                                className="bg-yellow-400 text-yellow-900 text-[9px] font-extrabold px-1 rounded leading-none py-0.5 shrink-0 flex items-center gap-0.5 shadow-sm"
                                title="Google Review already requested — please don't ask again"
                                data-testid={`review-asked-tag-${b.id}`}>
                                <Star className="w-2.5 h-2.5" strokeWidth={2.5} fill="currentColor" />
                              </span>
                            )}
                            {(b.customer_no_show_count || 0) > 0 && (
                              <span className="bg-red-600 text-white text-[9px] font-bold px-1 rounded leading-none py-0.5 shrink-0"
                                title={`${b.customer_no_show_count} no-show${b.customer_no_show_count > 1 ? "s" : ""}`}
                                data-testid={`no-show-tag-${b.id}`}>
                                ⚠{b.customer_no_show_count}
                              </span>
                            )}
                            {/* Loyalty badge: 20+ visits = VIP gold; 5+ = REGULAR.
                                Excludes today's booking from the count visually
                                by relying on the count already excluding pending. */}
                            {(b.customer_lifetime_visits || 0) >= 20 && (
                              <span className="bg-amber-400 text-amber-950 text-[9px] font-extrabold px-1.5 rounded leading-none py-0.5 shrink-0 shadow-sm"
                                title={`VIP — ${b.customer_lifetime_visits} lifetime visits`}
                                data-testid={`vip-tag-${b.id}`}>
                                ★VIP {b.customer_lifetime_visits}
                              </span>
                            )}
                            {(b.customer_lifetime_visits || 0) >= 5 && (b.customer_lifetime_visits || 0) < 20 && (
                              <span className="bg-emerald-500 text-white text-[9px] font-bold px-1 rounded leading-none py-0.5 shrink-0"
                                title={`Regular — ${b.customer_lifetime_visits} lifetime visits`}
                                data-testid={`regular-tag-${b.id}`}>
                                ✓{b.customer_lifetime_visits}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <div className="text-[11px] leading-snug font-medium opacity-95 mt-0.5 break-words" style={{ wordBreak: "break-word" }}>
                        {b.is_block_time ? (b.block_reason || "") : (
                          <ServicesSummaryWithRed summary={b.services_summary} red={b.no_time_services || []} multiline />
                        )}
                      </div>
                      <div className="text-[10px] font-mono opacity-90 leading-tight mt-0.5 font-semibold">
                        {formatTime12(b.start_time)} – {formatTime12(endTime)}
                      </div>
                      {/* Stamps */}
                      {stamps.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-auto pt-0.5">
                          {stamps.slice(0, 3).map((st) => (
                            <span key={st.key}
                              className="text-[8px] font-bold px-1 py-[1px] rounded uppercase tracking-wider leading-none"
                              style={{ background: "rgba(255,255,255,0.95)", color: st.color }}>
                              {st.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* "REQUESTED" stamp — rotated rubber-stamp style, bottom-right.
                        Stays visible AFTER check-in too (per owner's spec: tech
                        needs to know if the customer requested THIS specific
                        stylist even after they've been checked in). */}
                    {!b.is_block_time && !b.requested_any && (
                      <div className="absolute bottom-1 right-1 stamp-badge stamp-requested z-20 pointer-events-none" data-testid={`stamp-requested-${b.id}`}>
                        REQUESTED
                      </div>
                    )}
                    {/* "ASSIGN" stamp — green rubber-stamp for owner-assigned walk-ins.
                        Stays visible after check-in too. */}
                    {!b.is_block_time && b.requested_any && (
                      <div className="absolute bottom-1 right-1 stamp-badge stamp-assign z-20 pointer-events-none" data-testid={`stamp-assign-${b.id}`}>
                        ASSIGN
                      </div>
                    )}
                    {/* Resize handle (bottom edge) */}
                    <div data-resize-handle
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const startY = e.clientY;
                        const origDuration = b.duration_minutes;
                        const move = (ev) => {
                          ev.preventDefault();
                          const delta = ev.clientY - startY;
                          const deltaMin = Math.round(delta / PIXELS_PER_MIN / SLOT_MIN) * SLOT_MIN;
                          const newDur = Math.max(15, origDuration + deltaMin);
                          setResizing({ bookingId: b.id, currentDuration: newDur });
                        };
                        const up = () => {
                          window.removeEventListener("mousemove", move);
                          window.removeEventListener("mouseup", up);
                          // Mark that a resize JUST ended — used to suppress the click event
                          justResizedRef.current = true;
                          // Auto-clear flag if no click event arrives
                          setTimeout(() => { justResizedRef.current = false; }, 300);
                          setResizing((r) => {
                            if (r && r.bookingId === b.id && r.currentDuration !== origDuration) {
                              onResize(b, r.currentDuration);
                            }
                            return null;
                          });
                        };
                        window.addEventListener("mousemove", move);
                        window.addEventListener("mouseup", up);
                      }}
                      className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize hover:bg-black/10"
                      data-testid={`resize-${b.id}`}
                    />
                  </div>
                );
                });
              })()}
            </div>
          ))}
        </div>
      </div>
      {staff.length === 0 && <div className="p-8 text-center text-zen-muted">No active staff</div>}
    </div>
  );
}

function WeekView({ anchor, bookings, onClick, staff }) {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }).map((_, i) => addDays(start, i));
  return (
    <div className="bg-white rounded-2xl border border-zen-border overflow-hidden grid grid-cols-7">
      {days.map((d) => {
        const ds = fmtDate(d);
        const items = bookings.filter((b) => b.date === ds);
        return (
          <div key={ds} className="border-r border-zen-border min-h-[400px]">
            <div className="p-3 border-b border-zen-border text-xs uppercase tracking-[0.15em] text-zen-muted">
              {d.toLocaleDateString("en-US", { weekday: "short" })} <span className="text-zen-ink font-medium">{d.getDate()}</span>
            </div>
            <div className="p-2 space-y-1">
              {items.map((b) => {
                const c = bookingColor(b);
                return (
                  <button key={b.id} onClick={() => onClick(b)}
                    className="w-full text-left rounded-md px-2 py-1.5 border"
                    style={{ background: c.bg, borderColor: c.stripe, color: c.text }}
                    data-testid={`week-booking-${b.id}`}>
                    <div className="font-bold text-xs truncate">{formatTime12(b.start_time)} {b.is_block_time ? "Blocked" : b.customer_name}</div>
                    <div className="text-[11px] opacity-80 truncate">{staff.find((s) => s.id === b.staff_id)?.full_name}</div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, counts, onPick }) {
  const first = startOfMonth(anchor); const last = endOfMonth(anchor); const startCal = startOfWeek(first);
  const days = []; let d = new Date(startCal);
  while (d <= last || days.length % 7 !== 0) { days.push(new Date(d)); d = addDays(d, 1); }
  return (
    <div className="bg-white rounded-2xl border border-zen-border p-3">
      <div className="grid grid-cols-7 mb-2 text-xs uppercase tracking-[0.15em] text-zen-muted">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="p-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const ds = fmtDate(d); const cnt = counts[ds] || 0;
          return (
            <button key={i} onClick={() => onPick(d)}
              className={`p-3 rounded-lg text-left min-h-20 border ${inMonth ? "bg-white border-zen-border hover:border-zen-primary" : "bg-zen-bg/50 border-transparent text-zen-muted"}`}
              data-testid={`month-day-${ds}`}>
              <div className="text-sm font-medium">{d.getDate()}</div>
              {cnt > 0 && <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zen-surface text-[11px] text-zen-ink">{cnt} booking{cnt > 1 ? "s" : ""}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function durationFromTimes(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  const [sh, sm] = startStr.split(":").map(Number);
  const [eh, em] = endStr.split(":").map(Number);
  return Math.max(15, (eh * 60 + em) - (sh * 60 + sm));
}

function BookingDialog({ open, onClose, booking, creating, services, staff, onSaved, onCheckin, onConfirm, onNoShow, onDuplicate, onCancel, onRequestReview, onWalkinAssigned }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [picked, setPicked] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [mode, setMode] = useState("assign");
  const [busy, setBusy] = useState(false);
  // Block time state
  const [isBlock, setIsBlock] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [blockDuration, setBlockDuration] = useState(30);
  // Block ALL staff (holiday / salon-closed day). When true we ignore
  // the per-staff dropdown and write ONE block_times row with
  // staff_id="all". `is_slot_overlap` already honors that sentinel so
  // /public/availability returns zero slots even for the "Anyone" path.
  const [blockAll, setBlockAll] = useState(false);
  // Hierarchical service picker
  const [activeCategory, setActiveCategory] = useState(null);
  // Walk-in: suggested tech (from /api/find-tech)
  const [suggestedTech, setSuggestedTech] = useState(null); // { name, matchedService, staffId }
  const [findingTech, setFindingTech] = useState(false);
  // AI second opinion — auto-loads when reception picks services in walk-in mode
  const [aiSuggest, setAiSuggest] = useState(null); // { suggested:{name,reason}, alternatives:[], reasoning_summary }
  const [aiLoading, setAiLoading] = useState(false);
  // Lookup state for the phone → customer auto-fill (reception convenience).
  // Filled by /api/public/customer/{phone}; shows "Returning · no_show=N" badge.
  const [customerLookup, setCustomerLookup] = useState(null);
  // Fuzzy name suggestions — when reception types a name, query backend for
  // similar existing customer names (Levenshtein-based) to catch typos like
  // "Jedica" → "Jessica".
  const [nameSuggestions, setNameSuggestions] = useState([]);

  useEffect(() => {
    if (creating === "walk") {
      // Walk-in: time = current Florida time rounded up to next 15 min
      const now = new Date();
      const fl = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      let m = fl.getHours() * 60 + fl.getMinutes();
      m = Math.ceil(m / 15) * 15;
      const hh = String(Math.floor(m / 60) % 24).padStart(2, "0");
      const mm = String(m % 60).padStart(2, "0");
      setPhone(""); setName("Walk-in"); setNotes("");
      setPicked([]); setStaffId(""); setDate(fmtDate(new Date())); setTime(`${hh}:${mm}`); setMode("assign");
      setIsBlock(false); setBlockReason(""); setActiveCategory(null);
      setSuggestedTech(null);
    } else if (creating === "block") {
      setPhone(""); setName(""); setNotes("");
      setPicked([]); setStaffId(staff[0]?.id || ""); setDate(fmtDate(new Date())); setTime("10:00");
      setIsBlock(true); setBlockReason(""); setBlockDuration(30); setBlockAll(false); setActiveCategory(null);
      setSuggestedTech(null);
    } else if (creating && typeof creating === "object" && creating._duplicateFrom) {
      // Duplicate: clone every field from the source booking so reception can
      // immediately Save (or tweak time if there's a conflict). No need to
      // re-type phone/name/services.
      const src = creating._duplicateFrom;
      setPhone(src.customer_phone || "");
      setName(src.customer_name || "");
      setNotes(src.customer_notes || "");
      setPicked(src.service_ids || []);
      setStaffId(src.staff_id || "");
      setDate(src.date || fmtDate(new Date()));
      setTime(src.start_time || "10:00");
      setMode(src.requested_any ? "request" : "assign");
      setIsBlock(false); setBlockReason("");
      setActiveCategory(null);
      setSuggestedTech(null);
    } else if (creating && typeof creating === "object") {
      setPhone(""); setName(""); setNotes("");
      // Per owner: clicking a slot in a staff column means the CUSTOMER asked
      // for THIS specific stylist — default to "request" (red stripe) so the
      // booking carries the right intent and shows the REQUESTED rubber stamp.
      // Reception can still flip to "assign" via the toggle if they're just
      // placing a walk-in on whoever's free.
      setPicked([]); setStaffId(creating.staff_id || ""); setDate(creating.date || fmtDate(new Date()));
      setTime(creating.start_time || "10:00"); setMode("request");
      setIsBlock(false); setBlockReason("");
      // If a range was selected, infer duration for block default
      if (creating.end_time) setBlockDuration(durationFromTimes(creating.start_time, creating.end_time) || 30);
      setActiveCategory(null);
      setSuggestedTech(null);
    } else if (creating === true) {
      setPhone(""); setName(""); setNotes("");
      // "New" button (no specific staff column) — owner is doing an admin
      // walk-in / assign-anyone, default to "assign".
      setPicked([]); setStaffId(""); setDate(fmtDate(new Date())); setTime("10:00"); setMode("assign");
      setIsBlock(false); setBlockReason(""); setActiveCategory(null);
      setSuggestedTech(null);
    } else if (booking) {
      setPhone(booking.customer_phone || ""); setName(booking.customer_name || "");
      setNotes(booking.customer_notes || ""); setPicked(booking.service_ids || []);
      setStaffId(booking.staff_id); setDate(booking.date); setTime(booking.start_time);
      setMode(booking.requested_any ? "assign" : "request");
      setIsBlock(!!booking.is_block_time); setBlockReason(booking.block_reason || "");
      setBlockDuration(booking.duration_minutes || 30);
      setActiveCategory(null);
      setSuggestedTech(null);
    }
  }, [open, booking, creating]); // eslint-disable-line

  // Auto-fill customer name + show no-show warning when reception types a
  // 10-digit phone. Skips when editing an existing booking (don't overwrite
  // a customer name the user might be intentionally changing).
  useEffect(() => {
    if (!creating || isBlock || creating === "walk") { setCustomerLookup(null); return; }
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length !== 10) { setCustomerLookup(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/public/customer/${digits}`)
        .then(({ data }) => {
          if (cancelled) return;
          if (data.found) {
            setCustomerLookup(data);
            if (!name.trim()) setName(data.name || "");
          } else {
            setCustomerLookup({ found: false });
          }
        })
        .catch(() => { if (!cancelled) setCustomerLookup(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [phone, creating, isBlock]);

  // Fuzzy name typo detection — query backend whenever the typed name is
  // long enough and there's no exact match in the customer DB (best-effort,
  // debounced 400ms). Shows a "Did you mean ___?" pill so reception can
  // 1-tap-fix typos like "Jedica" → "Jessica".
  useEffect(() => {
    if (!creating || isBlock || creating === "walk") { setNameSuggestions([]); return; }
    const trimmed = (name || "").trim();
    if (trimmed.length < 3) { setNameSuggestions([]); return; }
    // If we already auto-filled from phone (exact returning customer), skip.
    if (customerLookup?.found && customerLookup.name === trimmed) { setNameSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/admin/customers/search`, { params: { q: trimmed, limit: 4 } })
        .then(({ data }) => {
          if (cancelled) return;
          // Hide perfect matches (no typo). Show only similar ≠ identical names.
          const sugs = (data.matches || []).filter(
            (m) => m.name.toLowerCase() !== trimmed.toLowerCase() && m.score < 0.99 && m.score >= 0.6
          );
          setNameSuggestions(sugs.slice(0, 3));
        })
        .catch(() => { if (!cancelled) setNameSuggestions([]); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [name, creating, isBlock, customerLookup]);

  // Auto-load AI suggestion when receptionist is in Walk-in mode and has
  // picked at least 1 service. Debounced so we don't spam the LLM.
  useEffect(() => {
    if (creating !== "walk" || picked.length === 0) {
      setAiSuggest(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setAiLoading(true);
      try {
        const { data } = await api.post("/ai/walkin-suggest", {
          service_ids: picked,
          customer_phone: (phone || "").trim(),
          customer_name: (name || "").trim(),
        });
        if (!cancelled && data.success) setAiSuggest(data);
      } catch (e) { /* AI is optional */ }
      finally { if (!cancelled) setAiLoading(false); }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, creating, phone]);

  // Group services by parent category for the hierarchical picker.
  // Preserve backend's sorted order (category_order + sort_order) instead of
  // alphabetizing — otherwise the admin's drag-to-reorder is ignored here.
  const categories = useMemo(() => {
    const map = {};
    const order = [];
    for (const s of services) {
      const key = s.parent_category || s.category || "Other";
      if (!map[key]) { map[key] = []; order.push(key); }
      map[key].push(s);
    }
    return order.map((k) => ({ name: k, items: map[k] }));
  }, [services]);

  // Walk-in: single-click — ask Turn Board who's next, save booking directly
  // onto that tech at the ceiling-rounded 15-min slot, flash their column yellow,
  // and close the dialog. One tap = done.
  const findAndSaveWalkin = async () => {
    if (picked.length === 0) { toast.error("Pick at least 1 service first"); return; }
    setFindingTech(true);
    try {
      // Step 1: ceiling round current Florida time to next 15-min
      const now = new Date();
      const fl = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      let m = fl.getHours() * 60 + fl.getMinutes();
      m = Math.ceil(m / 15) * 15;
      const walkinTime = `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
      const walkinDate = fmtDate(new Date());

      // Step 2: ask Turn Board for the next tech for these service categories.
      // Pass BOTH service_ids (enables time-budget aware matching with priority
      // P1=Acrylic Full Set/Builder/Tap Gel/Dip, P2=Pedicure, P3=Manicure, P4=Brow)
      // and the legacy `services` (parent categories) for back-compat.
      const pickedSvcs = picked.map((sid) => services.find((s) => s.id === sid)).filter(Boolean);
      const cats = Array.from(new Set(pickedSvcs.map((s) => s.parent_category || s.category || "Other")));
      const { data } = await api.post("/find-tech", { services: cats, service_ids: picked });
      if (!data.success) {
        toast.error(data.message || "No tech available for that service right now");
        return;
      }
      const techName = (data.tech?.name || "").trim().toLowerCase();
      const techPager = (data.tech?.pager || "").trim();
      // Match against Booking staff by pager FIRST (most reliable) and fall
      // back to case-insensitive name. This avoids the bug where a Turn-Board
      // entry with slightly different capitalization than the user record
      // ("Alice" vs "alice") silently fails to save the booking.
      const matchedStaff = staff.find((s) => (s.pager || "").trim() === techPager && techPager)
        || staff.find((s) => (s.full_name || "").trim().toLowerCase() === techName);
      if (!matchedStaff) {
        toast.error(`Turn-Board says "${data.tech.name}" but no matching Booking staff (pager=${techPager || "—"})`);
        return;
      }

      // Leftover services that don't fit time budget OR skill — both display in red
      const noTime = [...(data.no_time_services || []), ...(data.remaining || [])];

      // Step 3: save the booking on that staff. requested_any=TRUE so the
      // calendar paints it GREEN (walk-in / anyone-OK) — per Hong Phuc:
      // "tìm thợ thì auto tự động là assign nghĩa là màu xanh lá cây".
      const body = {
        customer_phone: (phone || "").trim(),
        customer_name: (name || "Walk-in").trim(),
        service_ids: picked,
        staff_id: matchedStaff.id,
        date: walkinDate,
        start_time: walkinTime,
        duration_minutes: pickedSvcs.reduce((acc, s) => acc + (s.duration_minutes || 0), 0) || 30,
        requested_any: true,
        no_time_services: noTime,
      };
      await api.post("/admin/bookings", body);

      // Step 4: persist the leftover red tag on the Turn Board entry (decays 60s
      // after the tech is Available again post-walk).
      if (noTime.length > 0 && data.tech?.id) {
        try {
          await api.post(`/turn-board/${data.tech.id}/pending-services`, { services: noTime });
        } catch (e) { /* non-blocking — booking already saved */ }
      }
      toast.success(`Walk-in saved for ${data.tech.name} at ${walkinTime}`);
      // Flash + close handled by parent
      onWalkinAssigned?.(matchedStaff.id, matchedStaff.full_name);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Walk-in failed");
    } finally {
      setFindingTech(false);
    }
  };

  const onSave = async () => {
    // Validation: when reception creates a new booking (not Hint, not Block),
    // phone is REQUIRED so we always link the booking to a customer record.
    if (creating && creating !== "walk" && !isBlock) {
      if (!phone || phone.replace(/\D/g, "").length < 10) {
        toast.error("Vui lòng nhập số điện thoại 10 số của khách");
        return;
      }
      if (!name.trim()) {
        toast.error("Vui lòng nhập tên khách");
        return;
      }
    }
    setBusy(true);
    try {
      if (creating) {
        // BLOCK ALL STAFF path — bypasses /admin/bookings (which requires a
        // specific staff). Write a single block_times row with
        // staff_id="all"; is_slot_overlap already treats "all" as blocking
        // every candidate stylist for that window so /public/availability
        // returns zero slots (even for the "Anyone" path). This is the fix
        // for the reported bug: previously the block dialog only accepted
        // ONE staff at a time, so owners forgot to block the other 12 —
        // customers picked "Anyone" and the free stylist accepted the
        // booking.
        if (isBlock && blockAll) {
          try {
            const startMin = parseInt(time.split(":")[0]) * 60 + parseInt(time.split(":")[1]);
            const endMin = startMin + (blockDuration || 30);
            const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
            await api.post(`/staff/block-time`, {
              staff_id: "all",
              dates: [date],
              start_time: time,
              end_time: endTime,
              reason: blockReason || "Closed / all staff blocked",
            });
            toast.success("All staff blocked for that time");
            onSaved();
            return;
          } catch (e) {
            toast.error(formatDetail(e.response?.data?.detail) || "Failed to block all staff");
            setBusy(false);
            return;
          }
        }
        const body = isBlock
          ? {
              customer_phone: "", customer_name: "Block Time",
              service_ids: [], staff_id: staffId, date, start_time: time,
              requested_any: false, is_block_time: true,
              duration_minutes: blockDuration, block_reason: blockReason,
            }
          : {
              customer_phone: phone || "0000000000", customer_name: name, customer_notes: notes,
              service_ids: picked, staff_id: staffId, date, start_time: time,
              requested_any: mode === "assign",
            };
        try {
          // Duplicate flow: customer already got the original booking SMS,
          // so skip sending a second "booking received" text per owner request.
          const skipSms = !!(creating && typeof creating === "object" && creating._duplicateFrom);
          const qs = skipSms ? "?skip_sms=true" : "";
          await api.post(`/admin/bookings${qs}`, body);
          toast.success(isBlock ? "Time blocked" : (skipSms ? "Duplicated" : "Created"));
        } catch (err) {
          const d = err.response?.data?.detail;
          if (typeof d === "object" && d?.code === "manual_conflict" && !isBlock) {
            const conflictsTxt = (d.conflicts || []).map((c) => `${c.customer_name || c.type} ${c.start_time}-${c.end_time}`).join("; ");
            if (window.confirm(`Slot has another manual booking (${conflictsTxt}). Save on top anyway?`)) {
              const skipSms2 = !!(creating && typeof creating === "object" && creating._duplicateFrom);
              const forceQs = skipSms2 ? "?force=true&skip_sms=true" : "?force=true";
              await api.post(`/admin/bookings${forceQs}`, body);
              toast.success("Created (overlapping)");
            } else { setBusy(false); return; }
          } else if (typeof d === "object" && d?.code === "online_conflict") {
            toast.error("Cannot save on top of an online/AI booking"); setBusy(false); return;
          } else {
            toast.error(formatDetail(d)); setBusy(false); return;
          }
        }
      } else {
        if (isBlock && booking?.is_block_time) {
          // Block-times have their own dedicated update endpoint because
          // they live in a separate collection. Compute end_time from
          // start + duration so the backend can store both bounds.
          const startMin = parseInt(time.split(":")[0]) * 60 + parseInt(time.split(":")[1]);
          const endMin = startMin + (blockDuration || 60);
          const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
          await api.patch(`/admin/block-time/${booking.id}`, {
            staff_id: staffId, date, start_time: time, end_time: endTime,
            reason: blockReason,
          });
          toast.success("Block updated");
        } else {
          const body = isBlock
            ? { staff_id: staffId, date, start_time: time, duration_minutes: blockDuration }
            : { staff_id: staffId, date, start_time: time, customer_notes: notes, requested_any: mode === "assign" };
          await api.patch(`/admin/bookings/${booking.id}`, body);
          toast.success("Saved");
        }
      }
      onSaved();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const titleText = creating === "walk" ? "Hint" : (isBlock ? "Block Time" : (creating ? "New Booking" : "Edit Booking"));
  const selectedStaff = staff.find((s) => s.id === staffId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle className="font-serif text-2xl">{titleText}</DialogTitle>
            {!creating && booking && !isBlock && (
              <div className="flex items-center gap-2">
                <Button onClick={() => onConfirm(booking.id)}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 h-10 text-sm font-bold"
                  data-testid="confirm-btn-big">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm
                </Button>
                <Button onClick={() => onCheckin(booking.id)}
                  className="rounded-full bg-red-600 hover:bg-red-700 text-white px-5 h-10 text-sm font-bold"
                  data-testid="checkin-btn-big">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Check-in
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Block-time toggle (only when creating) */}
        {creating && creating !== "walk" && (
          <div className="flex items-center gap-2 p-2 bg-zen-surface/60 rounded-xl">
            <Button
              variant={isBlock ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsBlock(false)}
              className={`rounded-full h-9 ${!isBlock ? "bg-zen-primary text-white" : ""}`}
              data-testid="dlg-mode-booking">
              Booking
            </Button>
            <Button
              variant={isBlock ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsBlock(true)}
              className={`rounded-full h-9 ${isBlock ? "bg-zen-ink text-white" : ""}`}
              data-testid="dlg-mode-block">
              <Ban className="w-3 h-3 mr-1" /> Block Time
            </Button>
            {isBlock && (
              <label className="ml-auto flex items-center gap-2 pr-3 pl-2 py-1 rounded-full bg-red-50 border-2 border-red-300 cursor-pointer select-none hover:bg-red-100 transition"
                title="Block ALL stylists for this window (holiday / closed day). Customers picking 'Anyone' will see NO slots."
                data-testid="dlg-block-all-label">
                <input
                  type="checkbox"
                  checked={blockAll}
                  onChange={(e) => setBlockAll(e.target.checked)}
                  className="w-4 h-4 accent-red-600"
                  data-testid="dlg-block-all-checkbox"
                />
                <span className="text-xs font-extrabold text-red-800 uppercase tracking-wide">Block ALL staff</span>
              </label>
            )}
          </div>
        )}

        <div className="space-y-3">
          {/* Phone & Name only when NOT block-time AND NOT hint mode (walk-in) */}
          {!isBlock && creating !== "walk" && (
            <>
              <Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Phone (10 digits) — REQUIRED *"
                className={`bg-zen-surface border-0 h-11 ${creating && (!phone || phone.length < 10) ? "ring-2 ring-red-300" : ""}`}
                data-testid="dlg-phone" />
              {customerLookup?.found && (
                <div className="flex flex-wrap items-center gap-2 text-xs px-1" data-testid="customer-lookup-badge">
                  <span className="bg-zen-cal-green text-white px-2 py-0.5 rounded-full font-bold">✓ Returning</span>
                  {/* Always show the saved name (one-tap to use it). Even
                      when the name field is already filled — reception can
                      see at a glance "this phone is registered as X" and
                      decide whether to overwrite. */}
                  {customerLookup.name && (
                    <button
                      type="button"
                      onClick={() => setName(customerLookup.name)}
                      className="bg-zen-primary/15 text-zen-primary px-2 py-0.5 rounded-full font-bold hover:bg-zen-primary/25"
                      title="Tap to fill the Name field with this saved name"
                      data-testid="customer-saved-name">
                      💾 {customerLookup.name}
                    </button>
                  )}
                  {customerLookup.no_show_count > 0 && (
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold" data-testid="no-show-warning">
                      ⚠ No-show {customerLookup.no_show_count}×
                    </span>
                  )}
                </div>
              )}
              {customerLookup && !customerLookup.found && phone.length === 10 && (
                <div className="text-xs text-zen-muted px-1">🆕 New customer — name will be saved for next time</div>
              )}
              <Input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Customer Name — REQUIRED *"
                className={`bg-zen-surface border-0 h-11 ${creating && !name.trim() ? "ring-2 ring-red-300" : ""}`}
                data-testid="dlg-name" />
              {nameSuggestions.length > 0 && (
                <div className="flex items-start gap-2 text-xs px-1" data-testid="name-suggestions">
                  <span className="text-zen-muted shrink-0 mt-1">💡 Did you mean</span>
                  <div className="flex flex-wrap gap-1.5">
                    {nameSuggestions.map((s) => (
                      <button key={s.phone || s.name} type="button"
                        onClick={() => { setName(s.name); if (s.phone && !phone) setPhone(s.phone); setNameSuggestions([]); }}
                        className="px-2 py-0.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 font-semibold border border-amber-300 transition"
                        data-testid={`name-suggest-${s.phone || s.name}`}>
                        {s.name}
                      </button>
                    ))}
                    <button type="button" onClick={() => setNameSuggestions([])}
                      className="text-zen-muted hover:text-zen-ink underline text-[10px] self-center">
                      keep
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Select value={blockAll ? "" : staffId} onValueChange={setStaffId} disabled={isBlock && blockAll}>
              <SelectTrigger className={`bg-zen-surface border-0 h-11 ${isBlock && blockAll ? "opacity-50" : ""}`} data-testid="dlg-staff">
                <SelectValue placeholder={isBlock && blockAll ? "ALL staff (closed day)" : "Stylist"} />
              </SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
            </Select>
            {!isBlock && (
              <div className="inline-flex rounded-full border border-zen-border p-0.5 bg-white" data-testid="mode-toggle">
                <button onClick={() => setMode("assign")} className={`flex-1 px-3 h-10 rounded-full text-xs font-bold ${mode === "assign" ? "bg-[#1B5E3C] text-white shadow" : "text-zen-muted hover:text-zen-ink"}`} data-testid="mode-assign">Assign</button>
                <button onClick={() => setMode("request")} className={`flex-1 px-3 h-10 rounded-full text-xs font-bold ${mode === "request" ? "bg-[#B22222] text-white shadow" : "text-zen-muted hover:text-zen-ink"}`} data-testid="mode-request">Request</button>
              </div>
            )}
            {isBlock && (
              <Select value={String(blockDuration)} onValueChange={(v) => setBlockDuration(parseInt(v))}>
                <SelectTrigger className="bg-zen-surface border-0 h-11" data-testid="dlg-block-duration"><SelectValue placeholder="Duration" /></SelectTrigger>
                <SelectContent>
                  {[15, 30, 45, 60, 90, 120, 180, 240, 360, 480].map((d) => (
                    <SelectItem key={d} value={String(d)}>{d} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="h-11 rounded-md bg-zen-surface px-3 text-left text-sm text-zen-ink flex items-center gap-2 hover:bg-zen-surface/80 transition"
                  data-testid="dlg-date">
                  <CalIcon className="w-4 h-4 text-zen-muted flex-shrink-0" />
                  <span className="truncate">
                    {date
                      ? new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                      : "Pick a date"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="start" data-testid="dlg-date-popover">
                <Calendar
                  mode="single"
                  selected={date ? new Date(date + "T12:00:00") : undefined}
                  onSelect={(d) => { if (d) setDate(fmtDate(d)); }}
                  weekStartsOn={1}
                  formatters={{
                    formatWeekdayName: (d) => ["S", "M", "T", "W", "T", "F", "S"][d.getDay()],
                  }}
                  className="p-3"
                  classNames={{
                    months: "flex flex-col space-y-3",
                    caption: "flex justify-center pt-1 relative items-center mb-2",
                    caption_label: "text-base font-serif text-zen-ink tracking-wide",
                    nav_button: "h-8 w-8 bg-white border border-zen-border rounded-full hover:border-zen-primary hover:bg-zen-primary hover:text-white inline-flex items-center justify-center transition",
                    table: "w-full border-collapse",
                    head_row: "flex mb-1",
                    head_cell: "text-zen-muted rounded-md w-9 font-semibold text-[11px] uppercase tracking-[0.15em]",
                    row: "flex w-full mt-1",
                    cell: "h-9 w-9 text-center text-sm p-0 relative",
                    day: "h-9 w-9 p-0 font-medium rounded-full hover:bg-zen-surface text-sm transition",
                    day_selected: "bg-zen-primary text-white hover:bg-zen-primary-hover hover:text-white shadow ring-2 ring-zen-primary/30",
                    day_today: "bg-zen-cal-yellow/15 text-zen-ink font-bold ring-1 ring-zen-cal-yellow/40",
                    day_outside: "text-zen-muted/30",
                    day_disabled: "text-zen-muted/25 cursor-not-allowed hover:bg-transparent",
                  }}
                />
              </PopoverContent>
            </Popover>
            <TimePicker value={time} onChange={setTime} placeholder="Start time" testIdPrefix="dlg-time" />
          </div>

          {isBlock ? (
            <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Reason (optional, e.g. Lunch, Personal, Off)"
              className="bg-zen-surface border-0 h-11" data-testid="dlg-block-reason" />
          ) : (
            <>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes"
                className="bg-zen-surface border-0 min-h-16" data-testid="dlg-notes" />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm uppercase tracking-[0.15em] text-zen-ink font-bold">Services</div>
                  {activeCategory && (
                    <button onClick={() => setActiveCategory(null)} className="text-sm text-zen-primary inline-flex items-center gap-1 font-medium" data-testid="svc-back-btn">
                      <ArrowLeft className="w-4 h-4" /> Back to categories
                    </button>
                  )}
                </div>

                {/* Step 1: Show parent categories — bigger tiles */}
                {!activeCategory && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-80 overflow-y-auto" data-testid="svc-category-list">
                    {categories.map((c) => (
                      <button key={c.name} onClick={() => setActiveCategory(c.name)}
                        className="p-4 rounded-xl border-2 bg-white border-zen-border hover:border-zen-primary hover:bg-zen-surface text-left transition"
                        data-testid={`svc-cat-${c.name}`}>
                        <div className="font-bold text-base text-zen-ink">{c.name}</div>
                        <div className="text-xs text-zen-muted mt-1">{c.items.length} services</div>
                      </button>
                    ))}
                    {categories.length === 0 && <div className="text-sm text-zen-muted col-span-3">No services yet</div>}
                  </div>
                )}

                {/* Step 2: Show services within active category — bigger, easier to read */}
                {activeCategory && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-80 overflow-y-auto">
                    {(categories.find((c) => c.name === activeCategory)?.items || []).map((s) => {
                      const skilled = !selectedStaff || selectedStaff.skills.includes(s.id);
                      const checked = picked.includes(s.id);
                      return (
                        <button key={s.id} disabled={!skilled}
                          onClick={() => {
                            setPicked((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id]);
                            // Auto-return to category list so user picks parent again to add another sub-service
                            setActiveCategory(null);
                          }}
                          className={`p-3 rounded-xl border-2 text-left transition ${!skilled ? "opacity-30 cursor-not-allowed bg-zen-surface/30" : checked ? "bg-zen-primary text-white border-zen-primary shadow-md" : "bg-white border-zen-border hover:border-zen-primary"}`}
                          data-testid={`dlg-service-${s.id}`}>
                          <div className="font-bold text-base leading-tight">{s.name}</div>
                          <div className={`text-xs mt-1 font-medium ${checked ? "opacity-90" : "text-zen-muted"}`}>
                            {s.duration_minutes}m {s.price > 0 && `· $${s.price}`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected services pill list — larger */}
                {picked.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {picked.map((sid) => {
                      const s = services.find((x) => x.id === sid);
                      if (!s) return null;
                      return (
                        <span key={sid} className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full bg-zen-primary text-white font-medium">
                          {s.name}
                          <button onClick={() => setPicked((p) => p.filter((x) => x !== sid))} className="opacity-80 hover:opacity-100 text-base leading-none">×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {!creating && booking && !booking.is_block_time && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-zen-border">
            <Button size="sm" variant="outline" onClick={() => onNoShow(booking.id)} className="rounded-full" data-testid="noshow-btn"><AlertTriangle className="w-3 h-3 mr-1" /> No-show</Button>
            <Button size="sm" variant="outline" onClick={() => onDuplicate && onDuplicate(booking)} className="rounded-full" data-testid="duplicate-btn"><Copy className="w-3 h-3 mr-1" /> Duplicate</Button>
            <Button size="sm" onClick={() => onRequestReview && onRequestReview(booking.id)}
              className="rounded-full bg-zen-cal-yellow/90 hover:bg-zen-cal-yellow text-white"
              data-testid="request-review-btn">
              <Star className="w-3 h-3 mr-1" fill="currentColor" /> Request Review
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onCancel(booking.id)} className="rounded-full text-red-600 ml-auto" data-testid="cancel-btn"><Trash2 className="w-3 h-3 mr-1" /> Cancel</Button>
          </div>
        )}
        {!creating && booking && booking.is_block_time && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-zen-border">
            <Button size="sm" variant="ghost" onClick={() => onCancel(booking.id)} className="rounded-full text-red-600 ml-auto" data-testid="cancel-block-btn"><Trash2 className="w-3 h-3 mr-1" /> Remove Block</Button>
          </div>
        )}
        {/* Walk-in: AI suggestion card (in-app), auto-refreshes when services change */}
        {creating === "walk" && (aiLoading || aiSuggest) && (
          <div className="mt-3 rounded-xl border border-zen-primary/30 bg-gradient-to-br from-zen-primary/5 to-zen-cal-yellow/10 p-3" data-testid="ai-suggest-card">
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-full bg-zen-primary text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-zen-primary font-bold mb-0.5">AI Suggests</div>
                {aiLoading && !aiSuggest && (
                  <div className="text-xs text-zen-muted italic">Analyzing techs &amp; today's schedule…</div>
                )}
                {aiSuggest?.suggested?.name && (
                  <>
                    <div className="text-sm text-zen-ink leading-tight">
                      <span className="font-bold text-base text-zen-primary">{aiSuggest.suggested.name}</span>
                      {aiSuggest.suggested.pager && <span className="text-[11px] font-mono text-zen-muted ml-1.5">#{aiSuggest.suggested.pager}</span>}
                    </div>
                    {aiSuggest.suggested.reason && (
                      <div className="text-xs text-zen-muted leading-snug mt-0.5" data-testid="ai-suggest-reason">{aiSuggest.suggested.reason}</div>
                    )}
                    {aiSuggest.alternatives?.length > 0 && (
                      <div className="text-[11px] text-zen-muted/80 mt-1.5 italic">
                        Backup: {aiSuggest.alternatives.map((a) => a.name).join(", ")}
                      </div>
                    )}
                  </>
                )}
                {aiSuggest && !aiSuggest.suggested && (
                  <div className="text-xs text-zen-muted italic">{aiSuggest.reasoning_summary || "No tech match found"}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Walk-in: replace footer with single find/save banner */}
        {creating === "walk" ? (
          <div className="border-t-2 border-dashed border-zen-primary/40 bg-zen-primary/5 -mx-6 px-6 -mb-6 pb-5 pt-4 mt-2 rounded-b-lg" data-testid="walkin-suggest-banner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-zen-ink min-w-0">
                <Sparkles className="w-4 h-4 text-zen-primary flex-shrink-0" />
                <span className="font-semibold">Walk-in:</span>
                <span className="text-zen-muted">Pick service(s) → tap "Find &amp; Save". The next-up tech is auto-picked and the booking is saved right under them.</span>
              </div>
              <Button
                onClick={findAndSaveWalkin}
                disabled={findingTech || busy || picked.length === 0}
                className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white px-6 h-11 font-bold shrink-0"
                data-testid="walkin-find-save-btn">
                <Sparkles className="w-4 h-4 mr-1.5" />
                {findingTech ? "Finding…" : "Find & Save"}
              </Button>
            </div>
          </div>
        ) : (
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="dlg-close">Close</Button>
            <Button onClick={onSave} disabled={busy} className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white px-8" data-testid="dlg-save">{busy ? "Saving…" : "Confirm"}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


function BookingQuickView({ booking, services, staff, onClose, onEdit, onDuplicate, onCheckin, onConfirm, onRequestReview }) {
  if (!booking) return null;
  const startMin = parseInt(booking.start_time.split(":")[0]) * 60 + parseInt(booking.start_time.split(":")[1]);
  const endMin = startMin + (booking.duration_minutes || 0);
  const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
  const dateObj = new Date(booking.date + "T00:00:00");
  const dateLabel = dateObj.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const tech = staff.find((s) => s.id === booking.staff_id);
  const svcList = (booking.service_ids || []).map((sid) => services.find((s) => s.id === sid)).filter(Boolean);
  const status = booking.checked_in ? { label: "CHECKED-IN", color: "bg-zen-muted/20 text-zen-ink" }
                : booking.status === "no_show" ? { label: "NO-SHOW", color: "bg-red-100 text-red-700" }
                : booking.status === "confirmed" ? { label: "CONFIRMED", color: "bg-emerald-100 text-emerald-700" }
                : !booking.requested_any ? { label: "REQUEST", color: "bg-amber-100 text-amber-800" }
                : { label: "BOOKED", color: "bg-zen-surface text-zen-ink" };
  return (
    <Dialog open={!!booking} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="booking-quickview">
        <DialogTitle className="sr-only">Booking Details</DialogTitle>
        {/* Header strip */}
        <div className="bg-zen-surface px-5 py-4 border-b border-zen-border flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-zen-muted font-semibold">{dateLabel}</div>
            <div className="font-serif text-xl text-zen-ink mt-1 leading-tight">
              {formatTime12(booking.start_time)} – {formatTime12(endTime)}
              <span className="text-sm text-zen-muted font-normal ml-2">({booking.duration_minutes}')</span>
            </div>
          </div>
          <button onClick={onEdit}
            className="w-9 h-9 rounded-full bg-white border border-zen-border hover:border-zen-primary hover:bg-zen-primary hover:text-white text-zen-ink flex items-center justify-center transition shadow-sm flex-shrink-0"
            data-testid="quickview-edit-btn"
            title="Edit booking">
            <Pencil className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          {/* Customer + status */}
          <div className="flex items-center justify-between gap-2">
            <div className="font-serif text-2xl text-zen-ink leading-tight truncate" data-testid="qv-name">
              {booking.customer_name || "Customer"}
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0 ${status.color}`}>
              {status.label}
            </span>
          </div>

          <Row icon={Phone} label="Phone" value={booking.customer_phone || "—"} testId="qv-phone" />
          <Row icon={UserIcon} label="Staff" value={(tech?.full_name || "—").toUpperCase()} testId="qv-staff" />
          <Row icon={Sparkles} label="Services"
            value={svcList.length ? svcList.map((s) => s.name).join(", ") : (booking.services_summary || "—")}
            testId="qv-services" />
          {(booking.customer_notes || "").trim() && (
            <div className="flex items-start gap-3" data-testid="qv-notes">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5 ring-1 ring-amber-300">
                <StickyNote className="w-4 h-4 text-amber-700" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-amber-700 font-bold">Note from customer</div>
                <div className="text-sm text-zen-ink font-medium leading-snug break-words whitespace-pre-wrap mt-0.5">
                  {booking.customer_notes}
                </div>
              </div>
            </div>
          )}
          {(booking.customer_blacklisted || (booking.customer_staff_note || "").trim()) && (
            <div className="flex items-start gap-3 rounded-xl bg-red-50 border-2 border-red-300 p-3" data-testid="qv-sodden">
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                <AlertTriangle className="w-4 h-4 text-white" strokeWidth={2.5} fill="currentColor" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-red-700 font-extrabold">
                  {booking.customer_blacklisted ? "⚠ Blacklisted — Sổ Đen" : "⚠ Sổ Đen / Staff warning"}
                </div>
                <div className="text-sm text-red-900 font-bold leading-snug break-words whitespace-pre-wrap mt-0.5">
                  {(booking.customer_staff_note || "").trim() || "This customer is on the blacklist. Please check with the owner before accepting."}
                </div>
              </div>
            </div>
          )}
          {booking.customer_review_requested && (
            <div className="flex items-center gap-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2" data-testid="qv-review-asked">
              <Star className="w-3.5 h-3.5 text-yellow-600" fill="currentColor" />
              <span className="font-semibold">Google review already requested — please don&apos;t ask this customer again.</span>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="px-5 py-3 bg-zen-bg border-t border-zen-border flex flex-wrap gap-2">
          {!booking.checked_in && (
            <Button size="sm" onClick={() => onCheckin(booking.id)}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white px-4 font-bold"
              data-testid="qv-checkin-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Check-in
            </Button>
          )}
          {booking.status !== "confirmed" && !booking.checked_in && (
            <Button size="sm" onClick={() => onConfirm(booking.id)}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 font-bold"
              data-testid="qv-confirm-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onRequestReview && onRequestReview(booking.id)}
            disabled={!!booking.customer_review_requested}
            className={`rounded-full ${booking.customer_review_requested
              ? "border-zen-border text-zen-muted opacity-70 cursor-not-allowed"
              : "border-zen-cal-yellow text-zen-cal-yellow hover:bg-zen-cal-yellow hover:text-white"}`}
            data-testid="qv-request-review-btn"
            title={booking.customer_review_requested ? "Already asked this customer for a Google review" : "Send Google review request SMS"}>
            <Star className="w-4 h-4 mr-1" fill="currentColor" /> {booking.customer_review_requested ? "Review asked" : "Review"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDuplicate && onDuplicate(booking)}
            className="rounded-full"
            data-testid="qv-duplicate-btn"
            title="Duplicate booking (same customer, same time/staff — pick services)">
            <Copy className="w-4 h-4 mr-1" /> Duplicate
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="rounded-full ml-auto" data-testid="qv-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="w-8 h-8 rounded-full bg-zen-surface flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-zen-muted" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.15em] text-zen-muted font-semibold">{label}</div>
        <div className="text-sm text-zen-ink font-medium leading-snug break-words">{value}</div>
      </div>
    </div>
  );
}
